import type { BakedManifest } from './bakedManifest';

export type BakedManifestFetcher = (url: string) => Promise<BakedManifest>;

async function fetchBakedManifest(url: string): Promise<BakedManifest> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Sprite manifest ${response.status}: ${url}`);
  return (await response.json()) as BakedManifest;
}

/**
 * Resolves baked sprite manifests from raw asset URLs one clip at a time.
 * Vite keeps the URL index in JavaScript, while the large frame tables stay as
 * independently cached JSON resources until a visible animation requests them.
 */
export class LazyBakedManifestStore {
  private readonly promises = new Map<string, Promise<BakedManifest | undefined>>();

  constructor(
    private readonly urls: ReadonlyMap<string, string>,
    private readonly fetchManifest: BakedManifestFetcher = fetchBakedManifest,
  ) {}

  load(key: string): Promise<BakedManifest | undefined> {
    const existing = this.promises.get(key);
    if (existing) return existing;
    const url = this.urls.get(key);
    if (!url) return Promise.resolve(undefined);
    const promise = this.fetchManifest(url).catch(() => undefined);
    this.promises.set(key, promise);
    return promise;
  }
}
